// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  FluentProvider,
  Spinner,
  Tab,
  TabList,
  Text,
  Title3,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  BeakerRegular,
  ChatRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  ClipboardRegular,
  CloudRegular,
  DataBarVerticalRegular,
  FolderRegular,
  PersonRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { IntlProvider, useIntl } from 'react-intl';
import { useAuth } from './auth/useAuth';
import { useApiToken } from './hooks/useApiToken';
import { useResolvedTheme } from './hooks/useResolvedTheme';
import { useUserPreferences } from './hooks/useUserPreferences';
import { useAutoZoom } from './hooks/useAutoZoom';
import { useServiceHealth } from './hooks/useServiceHealth';
import { useSidebarCollapse } from './hooks/useSidebarCollapse';
import { useHashNavigation } from './hooks/useHashNavigation';
import { TenantPicker } from './components/TenantPicker';
import { UserProfileMenu } from './components/UserProfileMenu';
import { ZoomControl } from './components/ZoomControl';
import { DeployBanner } from './components/DeployBanner';
import { fallbackMessages, FALLBACK_LOCALE } from './i18n/locales';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { CustomerSettingsPage } from './pages/CustomerSettingsPage';
import { DocumentViewerPage } from './pages/DocumentViewerPage';
import { PresentationViewerPage } from './pages/PresentationViewerPage';
import { AssessmentChatPage } from './pages/AssessmentChatPage';
import { ChatPage } from './pages/ChatPage';
import { SimulationResultsPage } from './pages/SimulationResultsPage';
import { RegistersPage } from './pages/RegistersPage';
import { CostEstimatePage } from './pages/CostEstimatePage';
import { AuditNavigatorPage } from './pages/AuditNavigatorPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotificationProvider, useNotifications } from './notifications/NotificationContext';
import { NotificationArea } from './notifications/NotificationArea';
import { listProjects, createProject, scaffoldDocuments, updateProject, listEngagementTypes } from './services/project-service';
import type { EngagementTypeOption, Project } from './services/project-service';
import { listDocuments, type MethodologyDocument } from './services/document-service';
import { listPresentations, type PresentationDocument } from './services/presentation-service';
import { listCustomers } from './services/customer-service';
import type { ActiveTab } from './types/navigation';
import type { UserPreferences } from './hooks/useUserPreferences';
import './App.css';

/** Maps a tab to its parent nav group, for auto-expand on navigation. */
const TAB_TO_GROUP: Record<string, string> = {
  projects: 'workspace', chat: 'workspace', registers: 'workspace',
  lab: 'tools',
  settings: 'system',
};

/**
 * Outer shell: resolves theme from user preferences, wraps the tree in
 * IntlProvider + FluentProvider + NotificationProvider. Per Shared-CSS-Reference
 * v3.1 §1.1.
 */
export default function App() {
  const { account } = useAuth();
  const userId = account?.homeAccountId?.split('.')[0];
  const { preferences, updatePreference } = useUserPreferences(userId);
  const theme = useResolvedTheme(preferences.theme);

  return (
    <IntlProvider locale={FALLBACK_LOCALE} messages={fallbackMessages} defaultLocale={FALLBACK_LOCALE}>
      <FluentProvider theme={theme}>
        <NotificationProvider>
          <AppContent preferences={preferences} onUpdatePreference={updatePreference} />
        </NotificationProvider>
      </FluentProvider>
    </IntlProvider>
  );
}

/**
 * Inner content: all UI that needs useIntl(). Holds shell state (activeTab,
 * nav groups, sidebar), auth, and project data. Renders one of: loading,
 * login, or the authenticated shell — per v3.1 §1.1.
 */
function AppContent({
  preferences,
  onUpdatePreference,
}: {
  preferences: UserPreferences;
  onUpdatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
}) {
  const intl = useIntl();
  const { isAuthenticated, isLoading, account, login, logout } = useAuth();
  const getToken = useApiToken();
  const hashNav = useHashNavigation();
  const { notify } = useNotifications();

  const userName = account?.name ?? 'User';
  const userEmail = account?.username;
  const userId = account?.homeAccountId?.split('.')[0];

  const zoomState = useAutoZoom(userId);
  const serviceHealth = useServiceHealth();
  const { collapsed: sidebarCollapsed, toggle: toggleSidebar } = useSidebarCollapse(userId);

  const appVersion = import.meta.env.VITE_APP_VERSION || 'dev';

  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [allDocs, setAllDocs] = useState<MethodologyDocument[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectDocs, setProjectDocs] = useState<MethodologyDocument[]>([]);
  const [projectPresentations, setProjectPresentations] = useState<PresentationDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<MethodologyDocument | null>(null);
  const [selectedPresentation, setSelectedPresentation] = useState<PresentationDocument | null>(null);
  const [engagementTypes, setEngagementTypes] = useState<EngagementTypeOption[]>([]);
  const [customerId, setCustomerId] = useState('');

  // Collapsible nav groups — first group expanded, deeper groups collapsed (R7).
  const NAV_GROUPS_KEY = userId ? `nav-groups-${userId}` : 'nav-groups';
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(userId ? `nav-groups-${userId}` : 'nav-groups');
      if (raw) {
        const parsed = JSON.parse(raw) as string[];
        if (parsed.length > 0) return new Set(parsed);
      }
    } catch { /* ignore */ }
    return new Set<string>(['tools', 'system']);
  });

  const toggleNavGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      try { localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, [NAV_GROUPS_KEY]);

  const expandGroupForTab = useCallback((tab: string) => {
    const group = TAB_TO_GROUP[tab];
    if (!group) return;
    setCollapsedGroups(prev => {
      if (!prev.has(group)) return prev;
      const next = new Set(prev);
      next.delete(group);
      try { localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, [NAV_GROUPS_KEY]);

  /** Collapsed group icon — expand the sidebar, open that group, select its first tab. */
  const expandGroup = useCallback((group: string, firstTab: ActiveTab) => {
    toggleSidebar();
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.delete(group);
      try { localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
    setActiveTab(firstTab);
  }, [toggleSidebar, NAV_GROUPS_KEY]);

  const fetchProjects = useCallback(async (cid: string): Promise<Project[]> => {
    try {
      const token = await getToken();
      return await listProjects(cid, token);
    } catch {
      return [];
    }
  }, [getToken]);

  const refreshAllProjects = useCallback(async () => {
    const allProjects: Project[] = [];
    const allDocuments: MethodologyDocument[] = [];
    const token = await getToken();

    let customerIds: string[];
    try {
      const customers = await listCustomers(token);
      customerIds = customers.map(c => c.id);
    } catch (err) {
      console.error('Failed to list customers — projects table will be empty:', err);
      setProjects([]);
      setAllDocs([]);
      setLoading(false);
      return;
    }

    for (const cid of customerIds) {
      const data = await fetchProjects(cid);
      allProjects.push(...data);
      for (const p of data) {
        if (p.id) {
          try {
            const docs = await listDocuments(cid, p.id, token);
            allDocuments.push(...docs);
          } catch { /* skip */ }
        }
      }
    }
    setProjects(allProjects);
    setAllDocs(allDocuments);
    setLoading(false);
  }, [fetchProjects, getToken]);

  // Load projects once authenticated.
  useEffect(() => {
    if (isAuthenticated) refreshAllProjects();
  }, [isAuthenticated, refreshAllProjects]);

  // Load the engagement-type catalogue once.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const types = await listEngagementTypes(token);
        if (!cancelled) setEngagementTypes(types);
      } catch (err) {
        console.error('Failed to load engagement types:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, getToken]);

  // Consume deep-link from the URL hash (e.g. #projects).
  useEffect(() => {
    if (hashNav.tab) {
      setActiveTab(hashNav.tab);
      expandGroupForTab(hashNav.tab);
      hashNav.clearParams();
    }
  }, [hashNav.tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateProject = async (project: Omit<Project, 'id'>) => {
    try {
      const token = await getToken();
      const created = await createProject(project as Project, token);
      if (created.id) {
        const scaffoldResult = await scaffoldDocuments(created.id, created.customerId, token);
        notify({
          intent: 'success',
          title: 'Project created',
          message: `${created.name} created. ${scaffoldResult.scaffolded} document${scaffoldResult.scaffolded === 1 ? '' : 's'} scaffolded.`,
        });
      } else {
        notify({ intent: 'success', message: `${created.name} created.` });
      }
      await refreshAllProjects();
      setActiveTab('projects');
    } catch (err) {
      console.error('Failed to create project:', err);
      notify({
        intent: 'error',
        title: 'Could not create project',
        message: err instanceof Error ? err.message : 'Unknown error.',
        durationMs: 0,
      });
    }
  };

  const handleUpdateProject = async (project: Project) => {
    try {
      const token = await getToken();
      const updated = await updateProject(project, token);
      await refreshAllProjects();
      if (selectedProject?.id === updated.id) {
        setSelectedProject(updated);
      }
      notify({ intent: 'success', message: `${updated.name} updated.` });
    } catch (err) {
      console.error('Failed to update project:', err);
      notify({
        intent: 'error',
        title: 'Could not update project',
        message: err instanceof Error ? err.message : 'Unknown error.',
        durationMs: 0,
      });
    }
  };

  const handleRescaffoldProject = async (project: Project) => {
    try {
      const token = await getToken();
      if (project.id) {
        const scaffoldResult = await scaffoldDocuments(project.id, project.customerId, token);
        notify({
          intent: 'success',
          title: 'Re-scaffold complete',
          message:
            scaffoldResult.scaffolded === 0
              ? 'No new documents scaffolded — every template already exists.'
              : `${scaffoldResult.scaffolded} new document${scaffoldResult.scaffolded === 1 ? '' : 's'} scaffolded.`,
        });
      }
      await refreshAllProjects();
    } catch (err) {
      console.error('Failed to re-scaffold project:', err);
      notify({
        intent: 'error',
        title: 'Could not re-scaffold project',
        message: err instanceof Error ? err.message : 'Unknown error.',
        durationMs: 0,
      });
    }
  };

  const handleSelectProject = async (project: Project) => {
    setSelectedProject(project);
    setSelectedDoc(null);
    setSelectedPresentation(null);
    setActiveTab('project-detail');
    try {
      const token = await getToken();
      const [docs, decks] = await Promise.all([
        listDocuments(project.customerId, project.id!, token),
        listPresentations(project.customerId, project.id!, token).catch(() => [] as PresentationDocument[]),
      ]);
      setProjectDocs(docs);
      setProjectPresentations(decks);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setProjectDocs([]);
      setProjectPresentations([]);
    }
  };

  const handleStartAssessment = () => setActiveTab('assessment-chat');

  const handleSelectDocument = (doc: MethodologyDocument) => {
    setSelectedDoc(doc);
    setActiveTab('document-viewer');
  };

  const handleSelectPresentation = (deck: PresentationDocument) => {
    setSelectedPresentation(deck);
    setActiveTab('document-viewer');
  };

  const handleBackToProject = () => {
    setSelectedDoc(null);
    setSelectedPresentation(null);
    setActiveTab('project-detail');
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setProjectDocs([]);
    setProjectPresentations([]);
    setActiveTab('projects');
  };

  const handleOpenRegisters = () => setActiveTab('registers');
  const handleOpenCostEstimate = () => setActiveTab('cost-estimate');
  const handleOpenAuditNavigator = () => setActiveTab('audit-navigator');
  const handleOpenCustomerSettings = () => setActiveTab('customer-settings');

  const onTabSelect = (_: unknown, data: { value: unknown }) => {
    const tab = data.value as ActiveTab;
    setActiveTab(tab);
    expandGroupForTab(tab);
  };

  const handleSignOut = () => { logout(); };

  // ── Auth loading state (v3.1 §1.1) ──
  if (isLoading) {
    return (
      <div className="page-loading page-loading--fullscreen">
        <Spinner size="large" label={intl.formatMessage({ id: 'auth.loading' })} />
      </div>
    );
  }

  // ── Login state (v3.1 §1.5) ──
  if (!isAuthenticated) {
    return (
      <div className="app-login">
        <CloudRegular style={{ fontSize: 48, color: tokens.colorBrandForeground1 }} />
        <Title3>{intl.formatMessage({ id: 'app.title' })}</Title3>
        <Text>{intl.formatMessage({ id: 'auth.login.prompt' })}</Text>
        <Button
          appearance="primary"
          icon={<PersonRegular />}
          size="large"
          onClick={login}
          disabled={isLoading}
        >
          {intl.formatMessage({ id: 'auth.login.button' })}
        </Button>
      </div>
    );
  }

  /** Top-level (un-grouped) tab label — hidden when collapsed. */
  const navLabel = (msgId: string) =>
    sidebarCollapsed ? null : <span className="nav-tab-label">{intl.formatMessage({ id: msgId })}</span>;
  const navTitle = (msgId: string) =>
    sidebarCollapsed ? intl.formatMessage({ id: msgId }) : undefined;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-left">
          <CloudRegular style={{ fontSize: 24, color: tokens.colorBrandForeground1 }} />
          <Title3>{intl.formatMessage({ id: 'app.title' })}</Title3>
        </div>
        <div className="user-info">
          <TenantPicker
            currentCustomerId={customerId}
            onTenantChange={setCustomerId}
            rememberOrganisation={preferences.rememberOrganisation}
            onToggleRemember={() => onUpdatePreference('rememberOrganisation', !preferences.rememberOrganisation)}
          />
          <ZoomControl zoomState={zoomState} />
          <UserProfileMenu
            accountName={userName}
            accountEmail={userEmail ?? ''}
            preferences={preferences}
            onUpdatePreference={onUpdatePreference}
            onSignOut={handleSignOut}
          />
        </div>
      </header>

      <DeployBanner status={serviceHealth.status} />

      <div className="app-body">
        <nav className={`app-nav${sidebarCollapsed ? ' app-nav--collapsed' : ''}`}>
          <TabList vertical size="medium" selectedValue={activeTab} onTabSelect={onTabSelect}>
            <Tab icon={<DataBarVerticalRegular />} value="dashboard" title={navTitle('nav.dashboard')}>
              {navLabel('nav.dashboard')}
            </Tab>
          </TabList>

          {/* ── Workspace group ── */}
          <div className="nav-divider" />
          {sidebarCollapsed ? (
            <Tooltip content={intl.formatMessage({ id: 'nav.group.workspace' })} relationship="label" positioning="after">
              <button type="button" className="nav-group-icon" aria-label={intl.formatMessage({ id: 'nav.group.workspace' })} onClick={() => expandGroup('workspace', 'projects')}>
                <FolderRegular />
              </button>
            </Tooltip>
          ) : (
            <>
              <button type="button" className="nav-group-header" onClick={() => toggleNavGroup('workspace')}
                aria-expanded={!collapsedGroups.has('workspace')}>
                {collapsedGroups.has('workspace')
                  ? <ChevronRightRegular className="nav-group-chevron" />
                  : <ChevronDownRegular className="nav-group-chevron" />}
                <span className="nav-group-title">{intl.formatMessage({ id: 'nav.group.workspace' })}</span>
              </button>
              {!collapsedGroups.has('workspace') && (
                <TabList vertical size="medium" selectedValue={activeTab} onTabSelect={onTabSelect}>
                  <Tab icon={<FolderRegular />} value="projects">{intl.formatMessage({ id: 'nav.projects' })}</Tab>
                  <Tab icon={<ChatRegular />} value="chat">{intl.formatMessage({ id: 'nav.chat' })}</Tab>
                  <Tab icon={<ClipboardRegular />} value="registers">{intl.formatMessage({ id: 'nav.registers' })}</Tab>
                </TabList>
              )}
            </>
          )}

          {/* ── Tools group ── */}
          <div className="nav-divider" />
          {sidebarCollapsed ? (
            <Tooltip content={intl.formatMessage({ id: 'nav.group.tools' })} relationship="label" positioning="after">
              <button type="button" className="nav-group-icon" aria-label={intl.formatMessage({ id: 'nav.group.tools' })} onClick={() => expandGroup('tools', 'lab')}>
                <BeakerRegular />
              </button>
            </Tooltip>
          ) : (
            <>
              <button type="button" className="nav-group-header" onClick={() => toggleNavGroup('tools')}
                aria-expanded={!collapsedGroups.has('tools')}>
                {collapsedGroups.has('tools')
                  ? <ChevronRightRegular className="nav-group-chevron" />
                  : <ChevronDownRegular className="nav-group-chevron" />}
                <span className="nav-group-title">{intl.formatMessage({ id: 'nav.group.tools' })}</span>
              </button>
              {!collapsedGroups.has('tools') && (
                <TabList vertical size="medium" selectedValue={activeTab} onTabSelect={onTabSelect}>
                  <Tab icon={<BeakerRegular />} value="lab">{intl.formatMessage({ id: 'nav.lab' })}</Tab>
                </TabList>
              )}
            </>
          )}

          {/* ── System group ── */}
          <div className="nav-divider" />
          {sidebarCollapsed ? (
            <Tooltip content={intl.formatMessage({ id: 'nav.group.system' })} relationship="label" positioning="after">
              <button type="button" className="nav-group-icon" aria-label={intl.formatMessage({ id: 'nav.group.system' })} onClick={() => expandGroup('system', 'settings')}>
                <SettingsRegular />
              </button>
            </Tooltip>
          ) : (
            <>
              <button type="button" className="nav-group-header" onClick={() => toggleNavGroup('system')}
                aria-expanded={!collapsedGroups.has('system')}>
                {collapsedGroups.has('system')
                  ? <ChevronRightRegular className="nav-group-chevron" />
                  : <ChevronDownRegular className="nav-group-chevron" />}
                <span className="nav-group-title">{intl.formatMessage({ id: 'nav.group.system' })}</span>
              </button>
              {!collapsedGroups.has('system') && (
                <TabList vertical size="medium" selectedValue={activeTab} onTabSelect={onTabSelect}>
                  <Tab icon={<SettingsRegular />} value="settings">{intl.formatMessage({ id: 'nav.settings' })}</Tab>
                </TabList>
              )}
            </>
          )}

          {!sidebarCollapsed && <div className="nav-version">{appVersion}</div>}
          <Tooltip content={sidebarCollapsed ? intl.formatMessage({ id: 'nav.expand' }) : intl.formatMessage({ id: 'nav.collapse' })} relationship="label">
            <div className="nav-collapse-toggle">
              <Button
                appearance="subtle"
                size="small"
                icon={sidebarCollapsed ? <ChevronDoubleRightRegular /> : <ChevronDoubleLeftRegular />}
                onClick={toggleSidebar}
              />
            </div>
          </Tooltip>
        </nav>

        <main ref={zoomState.contentRef} className="app-main" style={{ '--app-zoom': zoomState.zoom } as React.CSSProperties}>
          {loading && <div className="page-loading"><Spinner size="large" label="Loading projects..." /></div>}
          {!loading && activeTab === 'dashboard' && (
            <DashboardPage
              projects={projects}
              documentCounts={{
                total: allDocs.length,
                draft: allDocs.filter(d => d.status === 'draft').length,
                review: allDocs.filter(d => d.status === 'review').length,
                final: allDocs.filter(d => d.status === 'final').length,
              }}
              onSelectProject={handleSelectProject}
            />
          )}
          {!loading && activeTab === 'projects' && (
            <ProjectsPage
              projects={projects}
              engagementTypes={engagementTypes}
              onCreateProject={handleCreateProject}
              onSelectProject={handleSelectProject}
              onUpdateProject={handleUpdateProject}
              onRescaffoldProject={handleRescaffoldProject}
            />
          )}
          {activeTab === 'project-detail' && selectedProject && (
            <ProjectDetailPage
              project={selectedProject}
              documents={projectDocs}
              presentations={projectPresentations}
              engagementTypes={engagementTypes}
              onBack={handleBackToProjects}
              onSelectDocument={handleSelectDocument}
              onSelectPresentation={handleSelectPresentation}
              onStartAssessment={handleStartAssessment}
              onOpenRegisters={handleOpenRegisters}
              onOpenCostEstimate={handleOpenCostEstimate}
              onOpenAuditNavigator={handleOpenAuditNavigator}
              onOpenCustomerSettings={handleOpenCustomerSettings}
            />
          )}
          {activeTab === 'customer-settings' && selectedProject && (
            <CustomerSettingsPage
              customerId={selectedProject.customerId}
              onBack={handleBackToProject}
            />
          )}
          {activeTab === 'audit-navigator' && selectedProject && (
            <AuditNavigatorPage
              project={selectedProject}
              documents={projectDocs}
              onBack={handleBackToProject}
              onSelectDocument={handleSelectDocument}
            />
          )}
          {activeTab === 'document-viewer' && selectedDoc && !selectedPresentation && (
            <DocumentViewerPage
              document={selectedDoc}
              onBack={handleBackToProject}
            />
          )}
          {activeTab === 'document-viewer' && selectedPresentation && (
            <PresentationViewerPage
              presentation={selectedPresentation}
              onBack={handleBackToProject}
            />
          )}
          {activeTab === 'assessment-chat' && selectedProject && (
            <AssessmentChatPage
              project={selectedProject}
              onBack={handleBackToProject}
            />
          )}
          {activeTab === 'registers' && selectedProject && (
            <RegistersPage project={selectedProject} onBack={handleBackToProject} />
          )}
          {activeTab === 'registers' && !selectedProject && (
            <div className="page-root"><Text className="text-muted">Select a project first to view registers.</Text></div>
          )}
          {activeTab === 'cost-estimate' && selectedProject && (
            <CostEstimatePage project={selectedProject} onBack={handleBackToProject} />
          )}
          {!loading && activeTab === 'chat' && <ChatPage project={selectedProject} />}
          {!loading && activeTab === 'lab' && <SimulationResultsPage />}
          {activeTab === 'settings' && (
            <SettingsPage
              darkMode={preferences.theme === 'dark'}
              onToggleDarkMode={(v: boolean) => onUpdatePreference('theme', v ? 'dark' : 'light')}
              userName={userName}
              userEmail={userEmail}
              onSignOut={handleSignOut}
            />
          )}
        </main>
      </div>
      <NotificationArea />
    </div>
  );
}
